import { createApi } from '@reduxjs/toolkit/query/react';

import { axiosBaseQuery } from './axios-base-query';
import { actions } from '../redux';
// import { fetchBaseQuery } from './fetch-base-query';

export interface CreateProfileRequest {
  authUid: string;
  firstName: string;
  lastName: string;
  organization: string;
  jobTitle: string;
}

export interface AnchRole {
  id: number;
  name: string;
  isRolAplicative: boolean;
}

export interface AnchUserInfo {
  token?: string;
  userName?: string;
  firstName?: string;
  lastName?: string;
  personalityId?: number;
  role?: AnchRole[];
  customerId?: number;
  company?: string;
  connectedAt?: Date;
}

export interface UserProfile {
  access: string | false;
  joinDate: number | false;
  firstName: string | false;
  lastName: string | false;
  hasSelectedProfile: boolean;
  jobTitle: string | false;
  organization: string | false;
  role: string[] | false;
  emailVerified?: boolean;
  lastLogin: Date;
  orgId: string;
  email: string;
  otpEnabled: boolean;
  defaultOrganizationId: string;
  isSystemAdmin: boolean;
  isBetaUser: boolean;
  organizations: string[];
  currentOrganizationName: string;
  currentOrganizationRole?: 'ORG_ADMIN' | 'MEMBER';
  anchUserInfo?: AnchUserInfo;
}

export interface EditProfile {
  authUid: string;
  firstName: string;
  lastName: string;
  jobTitle: string;
}

export const userProfileApi = createApi({
  reducerPath: 'userProfileApi',
  baseQuery: axiosBaseQuery({ baseUrl: '/auth' }),
  tagTypes: ['user'],
  endpoints: (builder) => ({
    // Get user profile
    getProfile: builder.query<UserProfile, string>({
      providesTags: ['user'],
      query: (auth_uid) => {
        return {
          url: `/user-profile/${auth_uid}`,
          method: 'get',
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          /**
           * If access property missing, then user profile is returned is incomplete,
           * but it is still considered to be authenticated. (This happens when user
           * first registers the app, but has not provided org information)
           *
           * If access is available, then user is only authenticated when it is true.
           */
          const isAuthenticated = !('access' in data) || data.access;
          if (isAuthenticated) {
            dispatch(actions.userProfile.receiveSignedInUserProfile(data));
            // if (data.access) {
            //   dispatch(getQuestionnaireData());
            // }
          } else {
            dispatch(actions.userProfile.receiveSignedInUserProfile());
          }
        } catch (err) {
          /* ignore err */
        }
      },
    }),

    createProfile: builder.mutation<UserProfile, CreateProfileRequest>({
      query: (data) => {
        const formData = new FormData();
        formData.append('firstName', data.firstName);
        formData.append('lastName', data.lastName);
        formData.append('organization', data.organization);
        formData.append('jobTitle', data.jobTitle);
        return {
          url: '/users',
          method: 'POST',
          body: formData,
        };
      },
      async onQueryStarted({ authUid }, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        dispatch(actions.userProfile.receiveSignedInUserProfile(data));
        // Update getProfile cache with new values
        dispatch(
          userProfileApi.util.updateQueryData(
            'getProfile',
            authUid,
            (draft) => {
              draft.firstName = data.firstName;
              draft.lastName = data.lastName;
              draft.organization = data.organization;
              draft.jobTitle = data.jobTitle;
            }
          )
        );
      },
    }),

    updateProfile: builder.mutation<void, EditProfile>({
      query: (data) => {
        const formData = new FormData();
        formData.append('job_title', data.jobTitle);
        formData.append('first_name', data.firstName);
        formData.append('last_name', data.lastName);
        return {
          url: '/editProfile',
          method: 'PUT',
          body: formData,
        };
      },
      async onQueryStarted(
        { authUid, ...profile },
        { dispatch, queryFulfilled }
      ) {
        const { data } = await queryFulfilled;
        /**
         * data returned is:
         * {
         *   "Result": "Succesfully Updated"
         * }
         * which is useless for updating cache, unfortunately
         * So we used the args from the dispatch directly
         */
        dispatch(actions.userProfile.editProfile(profile));
        dispatch(
          userProfileApi.util.updateQueryData(
            'getProfile',
            authUid,
            (draft) => {
              draft.firstName = profile.firstName;
              draft.lastName = profile.lastName;
              draft.jobTitle = profile.jobTitle;
            }
          )
        );
      },
    }),
  }),
});
